import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Gift, Plus, Trophy } from "lucide-react";
import { Link } from "react-router-dom";

import { specialOfferApi } from "@/lib/extensions-api";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Tabs } from "@/components/ui/Tabs";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { CopyableId } from "@/components/ui/CopyableId";
import { CreateSpecialOfferModal } from "./CreateSpecialOfferModal";

export function SpecialOffersHubPage() {
  const [tab, setTab] = useState("active");
  const [createOpen, setCreateOpen] = useState(false);
  const [prefilledUser, setPrefilledUser] = useState<string | undefined>();
  const nav = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-special-offers", tab],
    queryFn: () => specialOfferApi.list(
      tab === "all" ? undefined : { status: tab }
    ),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Special Offers</h1>
          <p className="text-sm text-surface-500 mt-1">
            Reward top-performing customers with scheduled monetary payouts.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/special-offers/top-users">
            <Button variant="outline" leftIcon={<Trophy className="h-4 w-4" />}>
              Browse Top Users
            </Button>
          </Link>
          <Button leftIcon={<Plus className="h-4 w-4" />}
                   onClick={() => { setPrefilledUser(undefined); setCreateOpen(true); }}>
            New Offer
          </Button>
        </div>
      </header>

      <Card>
        <div className="px-5 pt-4">
          <Tabs value={tab} onChange={setTab} tabs={[
            { value: "active",    label: "Active" },
            { value: "paused",    label: "Paused" },
            { value: "completed", label: "Completed" },
            { value: "cancelled", label: "Cancelled" },
            { value: "all",       label: "All" },
          ]} />
        </div>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (data ?? []).length === 0 ? (
            <EmptyState icon={Gift} title="No offers here"
                          description="Create your first Special Offer above." />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>#</TH>
                  <TH>Target User</TH>
                  <TH className="text-right">Total</TH>
                  <TH className="text-right">Paid</TH>
                  <TH className="text-right">Daily</TH>
                  <TH>Duration</TH>
                  <TH>Status</TH>
                  <TH>Created</TH>
                </TR>
              </THead>
              <tbody>
                {data!.map((o) => (
                  <TR key={o.id} onClick={() => nav(`/admin/special-offers/${o.id}`)}>
                    <TD>#{o.id}</TD>
                    <TD>
                      <div>
                        <div className="text-sm font-medium">{o.target_full_name}</div>
                        <CopyableId value={o.target_user_id} />
                      </div>
                    </TD>
                    <TD className="text-right font-bold text-brand-700">
                      {formatCurrency(o.total_reward)}
                    </TD>
                    <TD className="text-right text-success-700 font-semibold">
                      {formatCurrency(o.paid_amount)}
                    </TD>
                    <TD className="text-right font-mono text-xs">
                      {formatCurrency(o.daily_amount)}
                    </TD>
                    <TD className="text-xs">{o.duration_days} days</TD>
                    <TD><StatusPill status={o.status} /></TD>
                    <TD className="text-xs text-surface-500">{formatDateTime(o.created_at)}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <CreateSpecialOfferModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        prefilledUserId={prefilledUser}
      />
    </div>
  );
}
